import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FamilyContactDialog } from "@/components/FamilyContactDialog";
import { Bell, CheckCheck, Trophy, ShieldAlert, Award, Info, FileText, UserX, PhoneCall, CheckCircle2 } from "lucide-react";

const ICONS = {
  DISCIPLINAR: ShieldAlert, PREMIACAO: Trophy, EXCELENCIA: Award,
  CERTIFICADO: FileText, INFO: Info, ALERTA_FALTAS: UserX,
};
const CONTACT_ROLES = ["SUPERADMIN", "DIRECAO", "COORDENACAO"];

function ContactAction({ n, canContact, onContact }) {
  if (n.type !== "ALERTA_FALTAS") return null;
  if (n.contact) return (
    <div className="mt-1 flex items-center gap-1 text-[11px] font-medium text-emerald-700" data-testid={`notification-contact-done-${n.id}`}>
      <CheckCircle2 className="h-3.5 w-3.5" /> Contato registrado por {n.contact.by}
    </div>
  );
  if (!canContact) return null;
  return (
    <span role="button" tabIndex={0} data-testid={`notification-contact-btn-${n.id}`}
      onClick={(e) => { e.stopPropagation(); onContact(n); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onContact(n); } }}
      className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-orange-500 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-orange-600">
      <PhoneCall className="h-3 w-3" /> Registrar contato com a família
    </span>
  );
}

export function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contactFor, setContactFor] = useState(null);
  const canContact = CONTACT_ROLES.includes(user?.role);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications);
      setUnread(data.unread);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 60000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.is_read) { try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ } load(); }
    if (n.link) navigate(n.link);
  };
  const readAll = async () => { try { await api.post("/notifications/read-all"); } catch { /* noop */ } load(); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notifications-bell" onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="relative grid h-9 w-9 place-items-center rounded-full transition hover:bg-slate-100">
        <Bell className="h-5 w-5 text-slate-600" />
        {unread > 0 && (
          <span data-testid="notifications-badge"
            className="absolute -right-0.5 -top-0.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notifications-panel"
          className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <span className="text-sm font-semibold text-slate-700">Notificações</span>
            <button onClick={readAll} disabled={!unread} data-testid="notifications-read-all"
              className="flex items-center gap-1 text-xs font-medium text-blue-700 transition disabled:text-slate-300">
              <CheckCheck className="h-3.5 w-3.5" /> Marcar todas
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-3 py-10 text-center text-sm text-slate-400">Sem notificações</div>
            ) : items.map((n) => {
              const Icon = ICONS[n.type] || Info;
              return (
                <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => openItem(n)}
                  className={`flex w-full gap-3 border-b border-slate-50 px-3 py-2.5 text-left transition hover:bg-blue-50 ${n.is_read ? "opacity-60" : ""}`}>
                  <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-blue-50 text-blue-700"><Icon className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-800">{n.title}</div>
                    <div className="text-xs leading-snug text-slate-500">{n.message}</div>
                    <ContactAction n={n} canContact={canContact} onContact={(x) => { setOpen(false); setContactFor(x); }} />
                  </div>
                  {!n.is_read && <span className="ml-auto mt-1 h-2 w-2 shrink-0 rounded-full bg-orange-500" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <FamilyContactDialog
        studentId={contactFor?.link?.split("/").pop()}
        description={contactFor?.message}
        open={!!contactFor}
        onClose={() => setContactFor(null)}
        onSaved={load}
      />
    </div>
  );
}
